import { get, post } from '../api/api.service';
import { getMimeTypeFromFileName, validateAttachment } from './attachment.utils';

// InvoiceFileUploadV2Controller's actions return their payload directly
// (Ok(await ...)) rather than wrapped in the {statusCode, message, response}
// envelope most of the rest of the API uses - these two types match that.
interface PresignedUploadUrlResponse {
  fileName: string;
  uploadUrl: string;
  expiresAt: string;
}

export interface PresignedUploadResult {
  fileName: string;
}

interface PresignedReadUrlResponse {
  url: string;
  expiresAt: string;
}

const getPresignedUploadUrlApi = async (
  fileName: string,
  contentType: string
): Promise<{ fileName: string; uploadUrl: string; expiresAt: string }> => {
  const response = await post<PresignedUploadUrlResponse>('/InvoiceFileUploadV2/presigned-upload-url', {
    fileName,
    contentType,
  });

  if (!response?.fileName || !response?.uploadUrl) {
    throw new Error('Failed to get presigned upload URL');
  }

  return response;
};

// Deliberately bypasses the shared apiClient (src/v2/api/api.service.ts): a SAS URL
// carries its own auth in the querystring, and Azure Blob's PUT needs the
// x-ms-blob-type header rather than a JSON content type — attaching our app's
// Bearer token here would be both unnecessary and wrong.
const putFileToBlobStorage = async (uploadUrl: string, file: File): Promise<void> => {
  const contentType = file.type || getMimeTypeFromFileName(file.name);

  const res = await fetch(uploadUrl, {
    method: 'PUT',
    headers: {
      'x-ms-blob-type': 'BlockBlob',
      'Content-Type': contentType,
    },
    body: file,
  });

  if (!res.ok) {
    throw new Error(`Blob upload failed with status ${res.status}`);
  }
};

/**
 * Uploads a single attachment directly to blob storage via a presigned URL and
 * returns the blob's server-generated filename to submit instead of the
 * file's base64 bytes. Throws on validation failure or any network error —
 * callers should catch and fall back to the legacy base64 path rather than
 * blocking the user.
 */
export const uploadAttachment = async (file: File): Promise<PresignedUploadResult> => {
  const { valid, errorKey } = validateAttachment(file);
  if (!valid) {
    throw new Error(errorKey || 'INVALID_ATTACHMENT');
  }

  const contentType = file.type || getMimeTypeFromFileName(file.name);
  const { fileName, uploadUrl } = await getPresignedUploadUrlApi(file.name, contentType);
  await putFileToBlobStorage(uploadUrl, file);

  return { fileName };
};

/**
 * Requests a short-lived, read-only SAS URL for the given blob name, so it can be
 * viewed without ever re-embedding bytes in a request body. Works just as well for
 * a freshly-uploaded, not-yet-saved attachment as for one already persisted — the
 * blob name is all either case needs. Call only on explicit user action (e.g.
 * clicking "View") — never pre-fetched.
 */
export const getAttachmentReadUrl = async (
  fileName: string
): Promise<{ url: string; expiresAt: string }> => {
  const response = await get('/InvoiceFileUploadV2/presigned-read-url', { fileName });
  const parsed = response as PresignedReadUrlResponse;

  if (!parsed?.url) {
    throw new Error('Failed to get attachment URL');
  }

  return parsed;
};
